// Third-party packages
import React, { useState, useContext, useEffect } from 'react'
import { useHistory, Link } from 'react-router-dom'
import {
  Button,
  Container,
  Form,
  Grid,
  Header,
  Icon,
  Message,
  Segment,
} from 'semantic-ui-react'

// Main packages
import * as api from '../api'
import { AppContext } from './app_context'
import SectionFooter from './section_footer'
import SectionHeader from './section_header'
import SectionTitle from './section_title'

export const PageRegister = () => {
  const history = useHistory()
  const { setLoading, refreshCart } = useContext(AppContext)
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    marketing_opt_in: false
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Check if user is already authenticated
  useEffect(() => {
    if (api.isAuthenticated()) {
      // User is already logged in, redirect to home page
      history.replace('/')
    }
  }, [history])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validatePassword = (password) => {
    const errors = []
    if (password.length < 8) {
      errors.push('at least 8 characters')
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('one uppercase letter')
    }
    if (!/[a-z]/.test(password)) {
      errors.push('one lowercase letter')
    }
    if (!/[0-9]/.test(password)) {
      errors.push('one number')
    }
    return errors
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required'
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      const passwordErrors = validatePassword(formData.password)
      if (passwordErrors.length > 0) {
        newErrors.password = `Password must contain ${passwordErrors.join(', ')}`
      }
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password'
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match'
    }
    
    if (formData.phone_number && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid phone number'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setLoading(true, 'registration')
    
    // Start user action for registration attempt
    let userAction = null;
    if (window.faroTracker && typeof window.faroTracker.startUserAction === 'function') {
      userAction = window.faroTracker.startUserAction('registration-attempt', {
        email: formData.email.toLowerCase().trim(),
        marketing_opt_in: formData.marketing_opt_in
      }, {
        triggerName: 'form_submit',
        importance: 'high'
      });
    }
    
    try {
      const startTime = Date.now()
      
      const registrationData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        phone_number: formData.phone_number.trim(),
        marketing_opt_in: formData.marketing_opt_in
      }
      
      const response = await api.customerRegister(registrationData)
      
      const duration = Date.now() - startTime
      
      if (response.status === 201 && response.data.message === 'success') {
        const { customer, token } = response.data
        
        // Store authentication data
        localStorage.setItem('auth_token', token)
        localStorage.setItem('customer_id', customer.id)
        localStorage.setItem('customer_email', customer.email)
        localStorage.setItem('customer_name', `${customer.first_name} ${customer.last_name}`)
        
        // Track successful registration
        if (window.faroTracker) {
          window.faroTracker.trackEvent('customer_registration_success', {
            customer_id: customer.id,
            email: customer.email,
            duration,
            marketing_opt_in: formData.marketing_opt_in,
            has_phone: !!formData.phone_number
          });
          
          // End user action with success
          if (userAction && typeof userAction.end === 'function') {
            userAction.end({ success: true, customer_id: customer.id });
          }
        }
        
        // Refresh cart with customer context
        await refreshCart(true)
        
        // Redirect to home page
        history.replace('/')
        
      } else {
        // Handle registration failure
        const errorMessage = response.data?.error || 'Registration failed. Please try again.'
        setErrors({ general: errorMessage })
        
        if (window.faroTracker) {
          window.faroTracker.trackEvent('customer_registration_failure', {
            error: errorMessage,
            email: formData.email,
            duration,
            failure_reason: 'validation_error'
          });
          
          // End user action with failure
          if (userAction && typeof userAction.end === 'function') {
            userAction.end({ success: false, error: errorMessage });
          }
        }
      }
    } catch (error) {
      console.error('Registration error:', error)
      let errorMessage = 'An unexpected error occurred. Please try again.'
      
      if (error.response?.status === 409) {
        errorMessage = 'An account with this email address already exists. Please sign in instead.'
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.error || 'Please check your information and try again.'
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }
      
      setErrors({ general: errorMessage })
      
      if (window.faroTracker) {
        window.faroTracker.trackEvent('customer_registration_error', {
          error: errorMessage,
          email: formData.email,
          error_type: error.response?.status || 'network_error'
        })
      }
    } finally {
      setIsSubmitting(false)
      setLoading(false, 'registration')
    }
  }

  return (
    <>
      <SectionHeader />
      <SectionTitle title="Create Account" subtitle="Join BitGeeks Coffee and start your journey" />
      
      <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
        <Grid centered>
          <Grid.Column mobile={16} tablet={12} computer={8}>
            <Segment raised>
              <Header as="h2" color="brown" textAlign="center">
                <Icon name="user plus" />
                Create Your Account
              </Header>
              
              {errors.general && (
                <Message negative>
                  <Message.Header>Registration Failed</Message.Header>
                  <p>{errors.general}</p>
                </Message>
              )}
              
              <Form onSubmit={handleSubmit} loading={isSubmitting}>
                <Form.Group widths="equal">
                  <Form.Field error={!!errors.first_name}>
                    <label>First Name *</label>
                    <Form.Input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                      icon="user"
                      iconPosition="left"
                      autoComplete="given-name"
                      autoFocus
                    />
                    {errors.first_name && (
                      <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                        {errors.first_name}
                      </div>
                    )}
                  </Form.Field>
                  
                  <Form.Field error={!!errors.last_name}>
                    <label>Last Name *</label>
                    <Form.Input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Enter your last name"
                      icon="user outline"
                      iconPosition="left"
                      autoComplete="family-name"
                    />
                    {errors.last_name && (
                      <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                        {errors.last_name}
                      </div>
                    )}
                  </Form.Field>
                </Form.Group>
                
                <Form.Field error={!!errors.email}>
                  <label>Email Address *</label>
                  <Form.Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    icon="mail"
                    iconPosition="left"
                    autoComplete="email"
                  />
                  {errors.email && (
                    <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                      {errors.email}
                    </div>
                  )}
                </Form.Field>
                
                <Form.Field error={!!errors.phone_number}>
                  <label>Phone Number (Optional)</label>
                  <Form.Input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    icon="phone"
                    iconPosition="left"
                    autoComplete="tel"
                  />
                  {errors.phone_number && (
                    <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                      {errors.phone_number}
                    </div>
                  )}
                </Form.Field>
                
                <Form.Group widths="equal">
                  <Form.Field error={!!errors.password}>
                    <label>Password *</label>
                    <Form.Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a strong password"
                      icon="lock"
                      iconPosition="left"
                      autoComplete="new-password"
                    />
                    {errors.password && (
                      <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                        {errors.password}
                      </div>
                    )}
                    <div style={{ color: '#767676', fontSize: '0.85em', marginTop: '0.25em' }}>
                      Must contain 8+ characters, uppercase, lowercase, and number
                    </div>
                  </Form.Field>
                  
                  <Form.Field error={!!errors.confirm_password}>
                    <label>Confirm Password *</label>
                    <Form.Input
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      icon="lock"
                      iconPosition="left"
                      autoComplete="new-password"
                    />
                    {errors.confirm_password && (
                      <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.25em' }}>
                        {errors.confirm_password}
                      </div>
                    )}
                  </Form.Field>
                </Form.Group>
                
                <Form.Field>
                  <Form.Checkbox
                    name="marketing_opt_in"
                    checked={formData.marketing_opt_in}
                    onChange={handleInputChange}
                    label="I would like to receive marketing emails about new products and offers"
                    disabled={isSubmitting}
                  />
                </Form.Field>
                
                <Button 
                  type="submit" 
                  color="blue" 
                  fluid 
                  size="large"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  <Icon name="user plus" />
                  Create Account
                </Button>
              </Form>
              
              <Segment secondary style={{ marginTop: '2em' }}>
                <Grid stackable>
                  <Grid.Column textAlign="center">
                    <p>
                      Already have an account?{' '}
                      <Link to="/login" style={{ color: '#2185d0', fontWeight: 'bold' }}>
                        Sign in here
                      </Link>
                    </p>
                  </Grid.Column>
                </Grid>
              </Segment>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>
      
      <SectionFooter />
    </>
  )
}
